import React from 'react';
import useSettings from '../hooks/useSettings';
import useKeybinds, { KEYBIND_LABELS, KEYBIND_GROUPS } from '../hooks/useKeybinds';
import './SettingsPanel.css';

/**
 * SettingsPanel — modal with Audio, Accessibility, and Display tabs.
 * Settings persist to localStorage via the settings utility module.
 */
const SettingsPanel = ({ onClose, onShowPatchNotes }) => {
  const [settings, update] = useSettings();
  // v640.5: Hotkey rebinding
  const { binds: keybinds, setBind, resetBinds } = useKeybinds();
  const [rebindingId, setRebindingId] = React.useState(null);

  // v642.8: Detect within-group conflicts so the user can spot duplicates.
  // Cross-group conflicts (e.g. overworld 'i' vs combat 'i') are intentional
  // and harmless — the two contexts never bind at the same time.
  const conflictIds = React.useMemo(() => {
    const dupes = new Set();
    for (const group of KEYBIND_GROUPS) {
      const seen = {};
      for (const id of group.ids) {
        const k = keybinds[id];
        if (!k) continue;
        if (seen[k]) {
          dupes.add(id);
          dupes.add(seen[k]);
        } else {
          seen[k] = id;
        }
      }
    }
    return dupes;
  }, [keybinds]);

  React.useEffect(() => {
    if (!rebindingId) return;
    const onKey = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.key === 'Escape') {
        setRebindingId(null);
        return;
      }
      // Only accept single printable letters/numbers (not modifiers, not arrow keys)
      if (e.key.length === 1 && /^[a-z0-9]$/i.test(e.key)) {
        setBind(rebindingId, e.key.toLowerCase());
        setRebindingId(null);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [rebindingId, setBind]);

  const fmt = (v) => Math.round(v * 100);

  // v679.2: brief arming delay so a stale click from whatever opened
  // the panel (e.g. the QuickSettingsPopover "More settings" button)
  // can't immediately bubble to the overlay's onClick={onClose} and
  // dismiss us on first paint.
  const [armed, setArmed] = React.useState(false);
  React.useEffect(() => {
    const t = setTimeout(() => setArmed(true), 120);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="settings-overlay" onClick={armed ? onClose : undefined}>
      <div className="settings-panel" onClick={(e) => e.stopPropagation()}>
        <div className="settings-header">
          <h2>⚙️ Settings</h2>
          <button className="settings-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="settings-body">
          {/* ── Audio ── */}
          <section className="settings-section">
            <h3>🔊 Audio</h3>
            <label className="settings-row">
              <span>Music Volume</span>
              <span className="settings-control">
                <input
                  type="range" min="0" max="100"
                  value={fmt(settings.musicVolume)}
                  disabled={settings.muteAll}
                  onChange={(e) => update({ musicVolume: Number(e.target.value) / 100 })}
                />
                <span className="settings-value">{fmt(settings.musicVolume)}%</span>
              </span>
            </label>
            <label className="settings-row">
              <span>Sound Effects</span>
              <span className="settings-control">
                <input
                  type="range" min="0" max="100"
                  value={fmt(settings.sfxVolume)}
                  disabled={settings.muteAll}
                  onChange={(e) => update({ sfxVolume: Number(e.target.value) / 100 })}
                />
                <span className="settings-value">{fmt(settings.sfxVolume)}%</span>
              </span>
            </label>
            <label className="settings-row settings-checkbox">
              <input
                type="checkbox"
                checked={settings.muteAll}
                onChange={(e) => update({ muteAll: e.target.checked })}
              />
              <span>Mute everything</span>
            </label>
          </section>

          {/* ── Accessibility ── */}
          <section className="settings-section">
            <h3>♿ Accessibility</h3>
            <label className="settings-row">
              <span>Colorblind Mode</span>
              <select
                className="settings-select"
                value={settings.colorblindMode}
                onChange={(e) => update({ colorblindMode: e.target.value })}
              >
                <option value="none">None (default)</option>
                <option value="protanopia">Protanopia (red-blind)</option>
                <option value="deuteranopia">Deuteranopia (green-blind)</option>
                <option value="tritanopia">Tritanopia (blue-blind)</option>
              </select>
            </label>
            <label className="settings-row">
              <span>Font Size</span>
              <select
                className="settings-select"
                value={settings.fontScale}
                onChange={(e) => update({ fontScale: e.target.value })}
              >
                <option value="small">Small</option>
                <option value="normal">Normal</option>
                <option value="large">Large</option>
              </select>
            </label>
            <label className="settings-row settings-checkbox">
              <input
                type="checkbox"
                checked={settings.reducedMotion}
                onChange={(e) => update({ reducedMotion: e.target.checked })}
              />
              <span>Reduce motion / animations</span>
            </label>
            <p className="settings-note">
              Colorblind modes apply a CSS filter that improves card and
              status-effect distinguishability. Use whichever feels best —
              they don't have to match a clinical diagnosis.
            </p>
          </section>

          {/* ── Keybinds (v640.5) ── */}
          <section className="settings-section">
            <h3>⌨️ Hotkeys</h3>
            <p className="settings-note" style={{ marginBottom: 8 }}>
              Click a key to rebind. Press any letter/number, or Esc to cancel.
              Movement (WASD/arrows) and number-row submenu shortcuts are not rebindable.
            </p>
            {KEYBIND_GROUPS.map(group => (
              <div key={group.label} style={{ marginBottom: 10 }}>
                <div style={{ color: '#aab8c8', fontSize: '0.85rem', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>
                  {group.label}
                </div>
                {group.ids.map(id => {
                  const isRebinding = rebindingId === id;
                  const isConflict = conflictIds.has(id);
                  return (
                    <label key={id} className="settings-row">
                      <span>
                        {KEYBIND_LABELS[id]}
                        {isConflict && !isRebinding && (
                          <span title="Conflicts with another binding in this group" style={{ marginLeft: 6, color: '#ff8888', fontSize: '0.78rem' }}>
                            ⚠ conflict
                          </span>
                        )}
                      </span>
                      <span className="settings-control">
                        <button
                          type="button"
                          onClick={() => setRebindingId(isRebinding ? null : id)}
                          style={{
                            minWidth: 56,
                            padding: '0.3rem 0.7rem',
                            background: isRebinding
                              ? 'rgba(255,200,80,0.2)'
                              : isConflict
                                ? 'rgba(160,60,60,0.35)'
                                : 'rgba(60,80,110,0.4)',
                            border: `1px solid ${
                              isRebinding ? '#ffc850' : isConflict ? '#d05858' : '#4a6a90'
                            }`,
                            color: isRebinding ? '#ffd96b' : isConflict ? '#ffb8b8' : '#d8e6f5',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontFamily: 'monospace',
                            fontSize: '0.95rem',
                            textTransform: 'uppercase',
                          }}
                        >
                          {isRebinding ? 'press a key…' : keybinds[id]}
                        </button>
                      </span>
                    </label>
                  );
                })}
              </div>
            ))}
            <button
              type="button"
              onClick={resetBinds}
              style={{
                marginTop: 6,
                padding: '0.4rem 0.9rem',
                background: 'rgba(120,60,60,0.35)',
                border: '1px solid #a04040',
                color: '#ffb8b8',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            >
              ↩️ Reset to defaults
            </button>
          </section>

          {/* ── Display ── */}
          <section className="settings-section">
            <h3>🖥️ Display</h3>
            <label className="settings-row settings-checkbox">
              <input
                type="checkbox"
                checked={!!settings.compactNumbers}
                onChange={(e) => update({ compactNumbers: e.target.checked })}
              />
              <span>Compact numbers (1.2k / 3.4M)</span>
            </label>
            <p className="settings-note">
              When on, gold, crowns, and XP totals are shown abbreviated.
              All settings are saved on this device.
            </p>
            {onShowPatchNotes && (
              <button
                onClick={onShowPatchNotes}
                style={{
                  marginTop: 8,
                  padding: '8px 14px',
                  background: 'rgba(201, 168, 76, 0.12)',
                  border: '1px solid #c9a84c66',
                  color: '#c9a84c',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: 12,
                }}
              >📰 Show patch notes</button>
            )}
          </section>
        </div>

        <div className="settings-footer">
          <button className="settings-btn settings-btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;
